import type { ButtonHTMLAttributes, ReactNode } from 'react'

import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly fullWidth?: boolean
  readonly children: ReactNode
}

/**
 * `type="button"` by default.
 *
 * The HTML default is `submit`, so a button inside a form that meant to open a
 * sheet submits it instead — which in this app means saving a half-filled
 * entry. Callers that want submission ask for it.
 */
export function Button({
  variant = 'secondary',
  fullWidth = false,
  type = 'button',
  className = '',
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    styles['button'],
    styles[variant],
    fullWidth ? styles['fullWidth'] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button type={type} className={classes} {...rest}>
      {children}
    </button>
  )
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Required, and there is no visible-text escape hatch.
   *
   * An icon-only control with no accessible name is announced as "button" and
   * is unusable with a screen reader. Making the prop mandatory is the only
   * version of this rule that survives contact with a deadline.
   */
  readonly label: string
  readonly children: ReactNode
}

export function IconButton({
  label,
  type = 'button',
  className = '',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      className={`${styles['icon']} ${className}`.trim()}
      aria-label={label}
      title={label}
      {...rest}
    >
      {children}
    </button>
  )
}
